/**
 * The interpretive report for a psychological safety survey, as one A4 document. The same page is
 * the source of the PDF: the export prints it and repeats its header and footer on every page.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Paragraph, Spinner } from '@digdir/designsystemet-react';
import { ArrowLeftIcon, DownloadIcon } from '@navikt/aksel-icons';
import { InterpretiveReportGenerationProgress } from '../../components/InterpretiveReportGenerationProgress/InterpretiveReportGenerationProgress';
import { useInterpretiveReportWithStatus } from '../../hooks/queries/useInterpretiveReportWithStatus';
import { Routes } from '../../enums/Routes';
import {
  dimensionView,
  localeOf,
  reportDate,
  scaleMax,
  sectionNumber,
} from './interpretiveReportView';
import { RunningFooter, RunningHeader } from './components/RunningHeaderFooter';
import { ReportCover } from './components/ReportCover';
import { ReportGuide } from './components/ReportGuide';
import { DimensionOverview } from './components/DimensionOverview';
import { DimensionSection } from './components/DimensionSection';
import { SpreadChart } from './components/SpreadChart';
import { DevelopmentPath } from './components/DevelopmentPath';
import { ConversationStarters } from './components/ConversationStarters';
import styles from './InterpretiveReportPage.module.css';

declare global {
  interface Window {
    /** Tells the PDF export that the report has rendered */
    __INTERPRETIVE_REPORT_READY__?: boolean;
  }
}

export const InterpretiveReportPage = () => {
  const { surveyId, teamId } = useParams<{ surveyId: string; teamId: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);
  // Someone who waited for the report gets to open it themselves, instead of the page switching under them
  const [watchedGeneration, setWatchedGeneration] = useState(false);

  const {
    report,
    isGenerating,
    isComplete,
    isFailed,
    isNotStarted,
    progress,
    step,
    startedAt,
    error: statusError,
    isLoading,
    refetchReport,
  } = useInterpretiveReportWithStatus(surveyId ? parseInt(surveyId, 10) : undefined);

  // The report is read in the language it was written in
  useEffect(() => {
    if (report?.language) {
      const i18nLanguage = report.language === 'NB' ? 'no' : 'en';
      if (i18n.language !== i18nLanguage) {
        i18n.changeLanguage(i18nLanguage);
      }
    }
  }, [report?.language, i18n]);

  useEffect(() => {
    if (isGenerating) setWatchedGeneration(true);
  }, [isGenerating]);

  useEffect(() => {
    if (report && isComplete) {
      const timer = setTimeout(() => {
        window.__INTERPRETIVE_REPORT_READY__ = true;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [report, isComplete]);

  const handleExportPdf = async () => {
    if (!surveyId || !teamId) return;

    setIsExporting(true);
    setExportFailed(false);
    try {
      const params = new URLSearchParams({ key: 'interpretive-report', surveyId, teamId });
      const response = await fetch(`/api/pdf-export?${params}`, { credentials: 'include' });
      const isPdf = response.headers.get('content-type')?.includes('application/pdf');
      if (!response.ok || !isPdf) throw new Error(`PDF export failed: ${response.status}`);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TrustPulse-Fortolkende-Rapport-${teamId}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch {
      setExportFailed(true);
    } finally {
      setIsExporting(false);
    }
  };

  const backToDashboard = () => navigate(Routes.DashboardPsychologicalSafety);

  const progressView = (state: { failed?: boolean; ready?: boolean } = {}) => (
    <InterpretiveReportGenerationProgress
      progress={progress}
      step={step}
      startedAt={startedAt}
      failed={state.failed ?? false}
      ready={state.ready ?? false}
      onBack={backToDashboard}
      onOpen={() => setWatchedGeneration(false)}
      onRetry={() => refetchReport()}
    />
  );

  if (isGenerating) return progressView();
  if (isFailed) return progressView({ failed: true });
  if (watchedGeneration && report) return progressView({ ready: true });

  if (isLoading) {
    return (
      <div className={styles.state}>
        <Spinner aria-label={t('interpretiveReport.loading')} />
        <Paragraph>{t('interpretiveReport.loading')}</Paragraph>
      </div>
    );
  }

  if (!report && !isNotStarted) {
    return (
      <div className={styles.state}>
        <Paragraph>{statusError || t('interpretiveReport.errorLoading')}</Paragraph>
        <Button variant="secondary" onClick={() => window.location.reload()}>
          {t('interpretiveReport.tryAgain')}
        </Button>
      </div>
    );
  }

  if (!report) return progressView();

  const locale = localeOf(report.language);
  const date = reportDate(report.createdAt, locale);
  const dimensions = report.dimensions.map((dimension) => dimensionView(dimension, locale));
  // Guide, overview, one page per dimension, spread, development path and conversations
  const firstDimension = 2;
  const afterDimensions = firstDimension + dimensions.length;

  return (
    <div className={styles.page} data-testid="interpretive-report">
      <div className={styles.toolbar} data-print="false">
        <Button variant="tertiary" onClick={backToDashboard}>
          <ArrowLeftIcon aria-hidden />
          {t('common.back')}
        </Button>
        <Button
          variant="secondary"
          onClick={handleExportPdf}
          loading={isExporting}
          data-testid="export-button"
        >
          {!isExporting && <DownloadIcon aria-hidden />}
          {isExporting ? t('interpretiveReport.export.exporting') : t('interpretiveReport.export.button')}
        </Button>
      </div>
      {exportFailed && (
        <div className={styles.toolbar} data-print="false">
          <Alert data-color="danger">{t('interpretiveReport.export.failed')}</Alert>
        </div>
      )}

      <article className={styles.paper}>
        <RunningHeader />

        <ReportCover report={report} locale={locale} date={date.long} />
        {report.dataQualityWarning && (
          <Alert
            data-color={report.dataQualityWarning.severity === 'CRITICAL' ? 'danger' : 'warning'}
            className={styles.keep}
          >
            {report.dataQualityWarning.message}
          </Alert>
        )}

        <ReportGuide number={sectionNumber(0)} />
        <DimensionOverview number={sectionNumber(1)} dimensions={dimensions} />
        {dimensions.map((dimension, index) => (
          <DimensionSection
            key={dimension.name}
            number={sectionNumber(firstDimension + index)}
            dimension={dimension}
            scaleMax={scaleMax(locale)}
          />
        ))}
        <SpreadChart number={sectionNumber(afterDimensions)} dimensions={dimensions} />
        <DevelopmentPath number={sectionNumber(afterDimensions + 1)} stage={report.developmentStage} />
        <ConversationStarters
          number={sectionNumber(afterDimensions + 2)}
          prompts={report.conversationPrompts}
        />

        <RunningFooter date={date.short} />
      </article>
    </div>
  );
};
